import React, { useState, useRef, useEffect } from 'react'

import { useTranslation } from 'react-i18next'

import PrimaryBannerPreviewContext from 'contexts/PrimaryBannerPreviewContext'

import useCookieBanner from 'hooks/useCookieBanner'

import helpers from 'helpers'
import constants from 'constants/index'

const PrimaryBannerPreviewProvider = ({
  children,
  size,
  isCompact,
  isMobile,
  privacyPortalEnabled,
  privacyPortalUrl,
  onManageCookies,
  handleOpenBanner,
  hideRejectButton,
}) => {
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false)
  const [isDescriptionOverflowing, setIsDescriptionOverflowing] =
    useState(false)

  const { TEXT_FIELDS, TEXT_FIELD_PLACEHOLDERS } =
    constants.cookies.bannerTextOverride

  const { t } = useTranslation()
  const { getBannerTextOverride, selectedLanguage } = useCookieBanner()
  const descriptionRef = useRef(null)

  const overrides = helpers?.cookies?.bannerTextOverride?.getBannerOverrides(
    getBannerTextOverride,
  )

  const mobileDefaults = TEXT_FIELD_PLACEHOLDERS[selectedLanguage] || {}

  const resolveButtonText = ({
    desktopOverride,
    mobileOverride,
    desktopDefault,
    mobileDefault,
  }) =>
    isMobile
      ? mobileOverride || desktopOverride || mobileDefault
      : desktopOverride || desktopDefault

  const previewTexts = {
    title: overrides.title || t('banner.title'),
    description:
      overrides.description ||
      t(isCompact ? 'banner.compactDescription' : 'banner.description'),
    manage: resolveButtonText({
      desktopOverride: overrides.manage,
      mobileOverride: overrides.manageMobile,
      desktopDefault: t('button.manageCookies'),
      mobileDefault: mobileDefaults[TEXT_FIELDS.BUTTON_MANAGE_COOKIES_MOBILE],
    }),
    reject: resolveButtonText({
      desktopOverride: overrides.reject,
      mobileOverride: overrides.rejectMobile,
      desktopDefault: t('button.rejectAll'),
      mobileDefault: mobileDefaults[TEXT_FIELDS.BUTTON_REJECT_MOBILE],
    }),
    accept: resolveButtonText({
      desktopOverride: overrides.accept,
      mobileOverride: overrides.acceptMobile,
      desktopDefault: t('button.acceptAll'),
      mobileDefault: mobileDefaults[TEXT_FIELDS.BUTTON_ACCEPT_ALL_MOBILE],
    }),
    privacyPortal: overrides.privacyPortal || t('privacyPortal.title'),
  }

  const isFixed = size === 'full'
  const isFull = isFixed && !isMobile

  const showDescriptionToggle =
    isDescriptionOverflowing || isDescriptionExpanded

  const onToggleDescription = () =>
    setIsDescriptionExpanded((previous) => !previous)

  useEffect(() => {
    if (isDescriptionExpanded) return
    const element = descriptionRef.current
    if (!element) return
    element.scrollTop = 0
    setIsDescriptionOverflowing(element.scrollHeight > element.clientHeight + 1)
  }, [previewTexts.description, isDescriptionExpanded, size, isCompact])

  return (
    <PrimaryBannerPreviewContext.Provider
      value={{
        size,
        isFixed,
        isFull,
        isCompact,
        isMobile,
        previewTexts,
        descriptionRef,
        isDescriptionExpanded,
        isDescriptionOverflowing,
        showDescriptionToggle,
        onToggleDescription,
        privacyPortalEnabled,
        privacyPortalUrl,
        onManageCookies,
        handleOpenBanner,
        hideRejectButton,
      }}
    >
      {children}
    </PrimaryBannerPreviewContext.Provider>
  )
}

export default PrimaryBannerPreviewProvider
